<template>
  <div
    v-if="section.conditions?.length"
    class="ca-list flex h-auto w-full flex-col space-y-4 rounded-sm p-2"
  >
    <div class="flex w-full items-center justify-between text-xs font-semibold">
      <span class="pt-0.5 uppercase">
        {{
          $t("ORGANIZATION.DO_CONDITIONAL_ARG", {
            num: Number(sectionIndex) + 1,
          })
        }}
      </span>
      <div
        v-on-click-outside="onClickOutsideHandler"
        class="relative flex items-center space-x-1"
      >
        <span
          v-if="!isActiveTemplatePublished"
          data-cy="add-attribute-dropdown-button"
          class="mr-3 cursor-pointer text-lg"
          @click="showAddAttributeDropdown = !showAddAttributeDropdown"
        >
          +
        </span>
        <add-attribute-dropdown
          v-if="showAddAttributeDropdown"
          class="top-full right-4 z-20"
          :attributes="attributes"
          :selected-attributes="selectedAttributes"
          @save="handleSaveAttributes"
        />
        <icon-base
          v-if="sectionIndex > 0 && !isActiveTemplatePublished"
          icon="delete"
          class="cursor-pointer text-disabled-medium"
          @click="handleSaveAttributes([])"
        />
      </div>
    </div>
    <conditional-argument
      v-for="(conditional, index) in section.conditions"
      :key="`section-${sectionIndex}-condition-${conditional.attribute}`"
      :argument="conditional"
      :current-step="currentOrchestrationStep"
      :section-index="sectionIndex"
      :is-draggable="false"
      :attribute-index="index"
      :is-last="index === section.conditions.length - 1"
      @remove="removeAttribute"
    />
  </div>
  <conditional-argument-empty
    v-else
    :step="currentOrchestrationStep"
    :argument-num="Number(sectionIndex) + 1"
    @save="handleSaveAttributes"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { SERVICE_CUSTOM } from "@/helpers/constants";
import { useDoActiveTemplateStatus } from "@/hooks/orchestration";

import AddAttributeDropdown from "./AddAttributeDropdown.vue";
import ConditionalArgument from "./ConditionalArgument.vue";
import ConditionalArgumentEmpty from "./ConditionalArgumentEmpty.vue";

import type {
  IContextSetValueModel,
  IDataOrchestrationFlowCheck,
  IDataOrchestrationFlowCheckCondition,
  IStep,
  IStepAttribute,
} from "@/models/orchestration";
import type { PropType } from "vue";

const props = defineProps({
  currentOrchestrationStep: {
    type: Object as PropType<IStep | null>,
    required: true,
  },
  section: {
    type: Object as PropType<IDataOrchestrationFlowCheck>,
    required: true,
  },
  sectionIndex: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits<{
  (eventName: "save", attributes: IStepAttribute[], sectionIndex: number): void;
}>();

const { isActiveTemplatePublished } = useDoActiveTemplateStatus();

const showAddAttributeDropdown = ref(false);

const attributes = computed(
  () =>
    props.currentOrchestrationStep?.attributes?.map((attribute) => ({
      id: attribute.id,
      ...(attribute.context && { context: attribute.context }),
    })) || []
);
const selectedAttributes = computed(() =>
  props.section.conditions.map((condition) => ({
    id: condition.attribute,
    ...(condition.context && { context: condition.context }),
  }))
);

const removeAttribute = (argument: IDataOrchestrationFlowCheckCondition) => {
  const toKeep = selectedAttributes.value.filter((selectedAttribute) =>
    argument.attribute === SERVICE_CUSTOM
      ? (selectedAttribute.context?.value as IContextSetValueModel)?.id !==
        (argument.context?.value as IContextSetValueModel).id
      : selectedAttribute.id !== argument.attribute
  );
  handleSaveAttributes(toKeep);
};

const handleSaveAttributes = (attributes: IStepAttribute[]) => {
  showAddAttributeDropdown.value = false;
  emit("save", attributes, props.sectionIndex);
};

const onClickOutsideHandler = [
  () => (showAddAttributeDropdown.value = false),
  { capture: false },
];
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.ca-list {
  border-width: 3px;
  border-color: rgba(55, 105, 214, 0.15);
}
</style>
